import { IssueStatus, OPEN_ISSUE_STATUSES } from '@supply-mover-2-0/shared';
import type {
	ColumnConfiguration,
	FilterRule,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../types';

/**
 * Who sees which issues, shared by every Issues tab: a user who may view any
 * issue starts on their own (assignee = Me, changeable); anyone else is held
 * to the issues on their own clients. A factory, like the other shared list
 * parts, so no two tabs share a mutable object.
 */
const issueScopeFilterRules = (): Record<string, FilterRule> => ({
	issueAssignee: {
		defaultValue: [{ id: 'ME', label: 'Me' }],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'issues:view:any', negated: false }],
		},
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'issues:view:any', negated: true }],
		},
	},
	issueClientSalesRep: {
		defaultValue: [{ id: 'ME', label: 'Me' }],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'issues:view:any', negated: true }],
		},
		lockedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'issues:view:any', negated: true }],
		},
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'issues:view:any', negated: true }],
		},
	},
});

/** The columns of the All Issues tab, reused by the other tabs. */
const issueColumns = (): Array<ColumnConfiguration> => [
	{
		id: 'issue-name-type',
		fields: [
			{
				id: 'name-field',
				link: {
					type: 'object',
					enabled: true,
					objectTable: 'issue',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: 'name', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'md',
					weight: 'medium',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				id: 'type-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'type', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-2',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				// PD-15442: where the issue came from ("CRM" or "Customer
				// Portal"). Not a stored column — IssueService derives the
				// label per row from the portal submitter foreign key, the same
				// column the `issueSource` filter tests, so the two cannot
				// disagree. It rides in the Name column as plain text (owner,
				// 2026-09-16) rather than as its own column, to keep the row
				// narrow; it stays selectable as a standalone field below.
				id: 'source-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'source', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		label: 'Name',
		headers: [{ id: 'h2', text: 'Name' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: 'issue' }],
		width: 'w-32',
	},
	{
		id: 'issue-status',
		fields: [
			{
				id: 'status-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'status', objectTable: 'issue' },
				},
				displayStyle: 'custom',
				customComponent: { id: 'issue-status-selector' },
			},
		],
		label: 'Status',
		headers: [{ id: 'h1', text: 'Status' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		sortFields: [
			{ direction: 'ASC', fieldName: 'status', objectTable: 'issue' },
		],
		width: 'w-16',
	},
	{
		id: 'issue-priority',
		fields: [
			{
				id: 'priority-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'priority', objectTable: 'issue' },
				},
				displayStyle: 'custom',
				customComponent: { id: 'issue-priority-selector' },
			},
		],
		label: 'Priority',
		headers: [{ id: 'h-priority', text: 'Priority' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		// PD-14789: priority first, then newest. Priority alone leaves ties
		// in insertion order, so an issue created from a record landed at
		// the bottom of its list, off-screen.
		sortFields: [
			{ direction: 'DESC', fieldName: 'priority', objectTable: 'issue' },
			{
				direction: 'DESC',
				fieldName: 'createdDate',
				objectTable: 'issue',
			},
		],
		width: 'w-24',
	},
	{
		id: 'issue-owner',
		fields: [
			{
				id: 'owner-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'assigneeUserName', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
		],
		label: 'Assigned To',
		headers: [{ id: 'h-owner', text: 'Assigned To' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		// PD-15834 — must match the virtual sort field the API handles
		// (IssueService.getIssues orders on the joined assignee's name).
		// This previously read `ownerUserName`, copied from the sourcing
		// request list; issues have an assignee, not an owner, so the
		// server emitted `ORDER BY issue.ownerUserName` and the whole
		// list failed with an unknown-column error.
		sortFields: [
			{
				direction: 'ASC',
				fieldName: 'assigneeUserName',
				objectTable: 'issue',
			},
		],
		width: 'w-28',
	},
	{
		// PD-16475 — the company the issue belongs to. Sorts on the
		// joined company's name (IssueService maps `companyName`).
		id: 'issue-company',
		fields: [
			{
				id: 'company-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'companyName', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
		],
		label: 'Company',
		headers: [{ id: 'h-company', text: 'Company' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		sortFields: [
			{
				direction: 'ASC',
				fieldName: 'companyName',
				objectTable: 'issue',
			},
		],
		width: 'w-28',
	},
	{
		id: 'issue-description',
		fields: [
			{
				id: 'description-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'description', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					textExpansion: { enabled: true, maxLines: 3 },
				},
				displayStyle: 'text',
			},
		],
		label: 'Description',
		headers: [{ id: 'h3', text: 'Description' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		width: 'flex-1',
	},
	{
		id: 'issue-created-date',
		fields: [
			{
				id: 'created-date-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'createdDate', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'date',
			},
			{
				id: 'created-line-break',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				// PD-14524 — creator name shown as a subtitle beneath the
				// created date, under the single "Created" header.
				id: 'created-by-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'creatorUserName', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		label: 'Created',
		headers: [{ id: 'h-created', text: 'Created' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		sortable: true,
		sortFields: [
			{
				direction: 'DESC',
				fieldName: '_created_date',
				objectTable: 'issue',
			},
		],
		width: 'w-32',
	},
	{
		id: 'related-entities',
		maxDisplay: 2,
		fields: [
			{
				id: 'related-clients',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'clients', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'related_entity_list',
				displayConfig: {
					entityType: 'client',
					icon: 'users',
					linkPattern: '/clients/{id}',
					nameField: 'name',
					separator: 'line_break',
				},
			},
			{
				id: 'related-vendors',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'vendors', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'related_entity_list',
				displayConfig: {
					entityType: 'vendor',
					icon: 'truck',
					linkPattern: '/vendors/{id}',
					nameField: 'name',
					separator: 'line_break',
				},
			},
			{
				id: 'related-merchandisers',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'merchandisers', objectTable: 'issue' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'related_entity_list',
				displayConfig: {
					entityType: 'merchandiser',
					icon: 'merchandiser',
					linkPattern: '/distribution/merchandisers/{id}',
					nameField: 'name',
					separator: 'line_break',
				},
			},
		],
		label: 'Related To',
		headers: [{ id: 'h4', text: 'Related To' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		width: 'w-48',
	},
	{
		id: 'issue-actions',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'edit-action',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'edit',
						variant: 'primary',
						visibility: 'visible',
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'delete',
						variant: 'danger',
						visibility: 'visible',
					},
				],
			},
		],
		label: 'Actions',
		headers: [{ id: 'h-actions', text: '' }],
		styleConfig: {
			size: 'md',
			colorRule: { type: 'default' },
		},
		width: 'w-24',
	},
];

/**
 * PD-16525 — how long a storefront customer has been waiting on the team.
 * `statusUpdatedDatetime` is when the request entered Needs Reply: a further
 * customer reply does not move it (the status is unchanged), so it marks the
 * first unanswered message. Shown as a date and time with "3 days ago" under
 * it, and the Needs Reply tab sorts on it oldest first.
 */
const WAITING_SINCE_COLUMN: ColumnConfiguration = {
	id: 'issue-waiting-since',
	fields: [
		{
			id: 'waiting-since-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'statusUpdatedDatetime', objectTable: 'issue' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'datetime',
		},
		{
			id: 'waiting-since-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'waiting-since-relative-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'statusUpdatedDatetime', objectTable: 'issue' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'relative_date',
		},
	],
	label: 'Waiting Since',
	headers: [{ id: 'h-waiting-since', text: 'Waiting Since' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'statusUpdatedDatetime',
			objectTable: 'issue',
		},
	],
	width: 'w-32',
};

/** One filter option per status, in the shape the status filter stores. */
const statusOptions = (
	statuses: ReadonlyArray<IssueStatus>,
): Array<{ id: string; label: string }> =>
	statuses.map((status) => ({ id: status, label: status }));

/**
 * PD-16525 — the team's reply queue: storefront support requests at Needs
 * Reply (the customer is waiting on the team), longest-waiting first. Both
 * filters are locked so the tab always means exactly that; the other filters
 * narrow it as usual. Shown only to tenants with an active storefront site:
 * `storefront` is derived by crm-api's FeatureFlagService from the tenant's
 * active portal sites. The `ecommerce` preference flag is not a reliable
 * signal for this (a tenant running a storefront can have it off).
 */
const needsReplyList: ListBuilderList = {
	id: 'default-issues-needs-reply',
	cacheTTL: 60000,
	name: 'Needs Reply',
	description:
		'Storefront support requests waiting on a reply from the team, longest-waiting first',
	type: 'default',
	active: true,
	sortOrder: 1,
	requiredFeature: 'storefront',
	filterRules: {
		issueStatus: {
			defaultValue: statusOptions([IssueStatus.NEEDS_REPLY]),
			locked: true,
		},
		issueSource: {
			defaultValue: [{ id: 'true', label: 'Customer Portal' }],
			locked: true,
		},
		...issueScopeFilterRules(),
	},
	// The All Issues columns with "Waiting Since" after Status.
	columns: issueColumns().flatMap((column) =>
		column.id === 'issue-status' ? [column, WAITING_SINCE_COLUMN] : [column],
	),
	defaultSortId: 'column-issue-waiting-since-asc',
	clickAction: {
		type: 'navigate',
		path: '/issues/{id}',
	},
};

/**
 * Issue list configuration
 * Main issues list with full features including filtering, sorting, and actions
 * Converted from JSON to TypeScript for type safety
 */
export const ISSUE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-issues-all',
			cacheTTL: 600000,
			name: 'All Issues',
			type: 'default',
			active: true,
			sortOrder: 0,
			filterRules: {
				// PD-16525 — every OPEN status: To-Do, Needs Reply, In Progress and
				// Waiting on Customer. Resolved (awaiting the customer's
				// confirmation) and Done are finished work.
				issueStatus: {
					defaultValue: statusOptions(OPEN_ISSUE_STATUSES),
				},
				...issueScopeFilterRules(),
			},
			columns: issueColumns(),
			defaultSortId: 'column-issue-priority-desc',
			clickAction: {
				type: 'navigate',
				path: '/issues/{id}',
			},
			actions: [
				{
					id: 'create-issue',
					label: 'Create Issue',
					icon: 'plus',
					type: 'create',
					position: 'header',
				},
			],
		},
		needsReplyList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'issueKeywords',
			'issueStatus',
			'issueType',
			'issuePriority',
			'issueSource',
			'issueClientSalesRep',
			'issueClient',
			'issueCompany',
			'issueAssignee',
			'issueCreatedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/issues/:id',
			objectLabel: 'Issue',
			objectTable: 'issue',
			// Rows on this list are issue records keyed by `id`, so any
			// configured column of the table can be filled in server-side
			// (ListFieldHydrationService).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				// PD-16525 — when the status last changed (the Needs Reply tab's
				// "Waiting Since").
				{ fieldName: 'statusUpdatedDatetime', fieldLabel: 'Status Updated' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
				{ fieldName: 'assigneeUserName', fieldLabel: 'Assigned To' },
				{ fieldName: 'creatorUserName', fieldLabel: 'Created By' },
				// PD-15442 — derived by the API from the portal submitter FK, not a
				// stored column on `issue`.
				{ fieldName: 'source', fieldLabel: 'Source' },
				// PD-16475 — the joined company's name; the stored column is
				// issue.fk_company__id.
				{ fieldName: 'companyName', fieldLabel: 'Company' },
			],
		},
	],
};
